<template>
  <!-- Global notification live region, render this permanently at the start of the document -->
  <div
    ref="liveRegionRef"
    aria-live="assertive"
    :class="[
      'pointer-events-none fixed inset-0 flex px-4 py-6 sm:p-6',
      outerPositionClasses
    ]"
    :style="overlayStyle"
  >
    <div
      ref="toastStackRef"
      :class="['flex w-full space-y-4 max-h-96 overflow-y-auto', innerPositionClasses]"
    >
      <!-- Overflow chip is placed at the visual edge furthest from the most-recent
           toast: for bottom-anchored stacks (flex-col-reverse → newest visually
           on top) the chip goes first in DOM so it sits at the visual bottom;
           for top-anchored stacks (flex-col → newest visually at bottom under
           current behavior) the chip goes last in DOM so it sits at the visual
           bottom. Either way, the chip never displaces the newest toasts. -->
      <OverflowChip
        v-if="overflowCount > 0 && isBottom"
        :count="overflowCount"
        :expanded="overflowExpanded"
        :class="depthClass"
        @toggle="toggleOverflow"
      />

      <component
        :is="notification.component"
        v-for="notification in visibleNotifications"
        :key="notification.props.id"
        v-bind="notification.props"
        :depth-class="depthClass"
        @close="(id) => notificationsStore.removeNotification(id)"
        @action="handleAction"
      />

      <OverflowChip
        v-if="overflowCount > 0 && !isBottom"
        :count="overflowCount"
        :expanded="overflowExpanded"
        :class="depthClass"
        @toggle="toggleOverflow"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
// The live region rides the chrome band of the app's one overlay ladder — the
// same band the global search bar wears. `useOverlay`'s `notifications-overlay`
// kind (registered `placed: false, obstacle: true`, overlayKinds.js) owns the
// band's z; the painted toast column feeds it a live obstacle rect.
import { useOverlay } from "@/composables/useOverlay";
import { useNotificationsStore } from "@/stores/notifications";
import { workspaceAdapter } from "@/services/workspaceAdapter";
import { landingRoute } from "@/utils/workspaceRoute";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import OverflowChip from "@/components/notifications/OverflowChip.vue";

const notificationsStore = useNotificationsStore();
const { activeNotifications, position, maxVisible } = storeToRefs(notificationsStore);

const router = useRouter();

// This live region renders permanently, so `open` is the literal `true`
// (useOverlay.js — a component whose life IS the open, not a ref that cycles).
const liveRegionRef = ref(null);
const toastStackRef = ref(null);

/**
 * The obstacle rect: the toast column as PAINTED, read only when another
 * overlay places. Never the live region's own box — that is a pointer-events-
 * none `fixed inset-0` wrapper, and as a permanent, first-registered obstacle
 * it made the whole viewport the one thing every later overlay had to clear,
 * so no candidate ever did (th-ilcxn). Never the stack's box either: it is
 * `w-full`, so it would block a full-width strip. The union of the rendered
 * toasts (and the overflow chip), clipped to the stack's scroll box — a toast
 * scrolled out of an expanded stack paints nothing — or `null` while nothing
 * renders, which the engine drops like any unmeasured box.
 */
function paintedToastColumnRect() {
  const stack = toastStackRef.value;
  if (!stack) return null;

  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const toast of stack.children) {
    const box = toast.getBoundingClientRect();
    if (!(box.width > 0 && box.height > 0)) continue;
    left = Math.min(left, box.left);
    top = Math.min(top, box.top);
    right = Math.max(right, box.right);
    bottom = Math.max(bottom, box.bottom);
  }

  const scrollBox = stack.getBoundingClientRect();
  left = Math.max(left, scrollBox.left);
  top = Math.max(top, scrollBox.top);
  right = Math.min(right, scrollBox.right);
  bottom = Math.min(bottom, scrollBox.bottom);
  if (!(right > left && bottom > top)) return null;

  return { x: left, y: top, left, top, right, bottom, width: right - left, height: bottom - top };
}

// The live region is a full-viewport wrapper and casts no shadow itself: the
// band's depth (`depthClass`) is handed to each toast and to the overflow chip,
// the surfaces that float, so neither spells a shadow of its own.
const { style: overlayStyle, depthClass } = useOverlay({
  kind: "notifications-overlay",
  open: true,
  floating: liveRegionRef,
  rect: paintedToastColumnRect,
});

const isBottom = computed(
  () => position.value === 'bottom-left' || position.value === 'bottom-right'
);

const overflowExpanded = ref(false);

const overflowCount = computed(() => {
  const total = activeNotifications.value.length;
  return Math.max(0, total - maxVisible.value);
});

// Severity rank for visible-window selection (bead th-ols1d). Errors and
// warnings displace older info/success toasts so a flood of low-priority
// toasts never drowns a critical message. Within the same severity, recency
// still wins (matches the existing flex-col-reverse "newest visually on top"
// behavior). The store and Artemis envelope still see the full list — this
// is purely a rendering policy.
const SEVERITY_RANK = { error: 3, warning: 2, success: 1, info: 0 };

function severityRank(type) {
  return SEVERITY_RANK[type] ?? 0;
}

// When expanded, render the full list; when collapsed, render the top
// `maxVisible` entries chosen by (severity desc, recency desc), preserving
// the array's insertion order so the rendered flow matches the existing
// directional behavior.
const visibleNotifications = computed(() => {
  if (overflowExpanded.value) return activeNotifications.value;
  if (overflowCount.value === 0) return activeNotifications.value;

  const indexed = activeNotifications.value.map((n, i) => ({ n, i }));
  indexed.sort((a, b) => {
    const sevDelta = severityRank(b.n.props.type) - severityRank(a.n.props.type);
    if (sevDelta !== 0) return sevDelta;
    return b.i - a.i; // more recent first within same severity
  });
  const keep = new Set(indexed.slice(0, maxVisible.value).map(({ n }) => n.id));
  return activeNotifications.value.filter((n) => keep.has(n.id));
});

// Auto-collapse once the hidden tail drains (dismissals, autoHide). Without
// this, the chip would already be hidden but `overflowExpanded` would stay
// true silently — fine for state, but means the next overflow burst would
// render expanded by default.
watch(overflowCount, (count) => {
  if (count === 0 && overflowExpanded.value) {
    overflowExpanded.value = false;
  }
});

function toggleOverflow() {
  overflowExpanded.value = !overflowExpanded.value;
}

const outerPositionClasses = computed(() => {
  switch (position.value) {
    case 'bottom-right':
      return 'items-end justify-end sm:items-end sm:justify-end';
    case 'top-right':
      return 'items-start justify-end sm:items-start sm:justify-end';
    case 'top-left':
      return 'items-start justify-start sm:items-start sm:justify-start';
    case 'bottom-left':
    default:
      return 'items-end justify-start sm:items-end sm:justify-start';
  }
});

const innerPositionClasses = computed(() => {
  const isLeft = position.value === 'bottom-left' || position.value === 'top-left';

  const direction = isBottom.value ? 'flex-col-reverse' : 'flex-col';
  const alignment = isLeft ? 'items-start' : 'items-end';

  return `${direction} ${alignment}`;
});

// The overlay is in the thin shell every route boots (launch-marketing-pages
// design M2), so a toast's action reaches the workspace only through the
// adapter the workspace registers. With none (a public page no workspace has
// mounted in), "View hub" moves to the workspace with We on its `?state=`,
// which the workspace that mounts reads as a page load does.
function handleAction(actionId) {
  if (actionId === 'viewHub') {
    const workspace = workspaceAdapter();
    if (workspace) {
      workspace.openTab('We');
    } else {
      router.push(
        landingRoute({
          location: { name: 'MainMap' },
          workspaceState: { secondaryPanel: { horizontalTab: 'We' } },
        }),
      );
    }
  }
}
</script>
